import BottomNav from '@/components/bottom-nav';
import LoadingComponent from '@/components/loading-component';
import * as Constants from '@/constants';
import EventBus from '@/event-bus';
import api from '@/utils/api';
import utilityFunctions from '@/utils/functions';
import SubmissionClass from '@/utils/service-log-submission-class';
import {
  areMinutesGreaterThanZero,
  isBetweenStartAndEndDate,
  isEndTimeEndGreaterThanStartTime,
  isNotFutureDate,
  isProcedureEndTimeWithinSession,
  isProcedureStartTimeWithinSession,
  isSessionTimeEndGreaterThanSessionTime,
  isTotalReimbursableMinutesValid,
  isValidMinutes,
  isValidTime,
  isWithinOneYear
} from '@/utils/validations';
import * as moment from 'moment';
import Swal from 'sweetalert2';
import { required, requiredIf } from 'vuelidate/lib/validators';
import ExtendedServiceInformation from '../extended-service-information/extended-service-information.vue';
import MedicaidInformation from '../medicaid-information/medicaid-information.vue';
import ServiceInformation from '../service-information/service-information.vue';
import StudentCard from '../student-card/student-card.vue';

export default {
  name: 'SlSteppers',
  components: {
    StudentCard,
    ServiceInformation,
    MedicaidInformation,
    BottomNav,
    LoadingComponent,
    ExtendedServiceInformation
  },
  props: ['student', 'medicaidArray', 'needLevelArray', 'deliveryModeArray', 'showTooltip', 'index', 'arrayLength', 'action', 'loadData', 'resetValidations', 'supervisorFlagsArray'],
  data() {
    return {
      errorMsg: '',
      // props for medicaid component
      saveOptions: '',
      // submitOptions
      save: false,
      saveAndNext: false,
      saveAndClone: false,
      showLoading: false,
      constants: Constants,
      toaster: false,
      toasterMsg: '',
      timeout: 2000,
      totalMinutesError: ''
    };
  },
  computed: {
    isMobile() {
      return this.$mq === 'mobile';
    },
    isDraft() {
      return !this.student.form.serviceInformation.licenseAttestUserId;
    },
    totalReimbursableMinutesErrors() {
      const errors = [];
      const totalMinutes = this.$v.student.form.totalReimbursableMinutes;
      if (!totalMinutes || !totalMinutes.$dirty) return errors;
      if (!totalMinutes.isTotalReimbursableMinutesValid) errors.push('Total Reimbursable Minutes must be less than or equal to Service Length.');
      return errors;
    },
    showSaveClone() {
      try {
        const threshold = moment(Constants.PROCEDURE_START_TIME);

        const now = moment();
        const referenceDate = now.isSameOrAfter(threshold) ? threshold : now;
        let baseline;
        if (this.action === 'create' || !this.student || !this.student.form) {
          baseline = moment();
        } else {
          // ServiceLogClass stores updatedDate at top-level of form
          const updated = this.student.form.updatedDate;
          baseline = updated ? moment(updated) : moment();
        }

        return baseline.isBefore(referenceDate);
      } catch (e) {
        return true;
      }
    }
  },
  validations() {
    const isFinal = () => {
      return !!this.student.form.serviceInformation.licenseAttestUserId;
    };
    // Documentation Of Service (medicaid) is allowed to be entirely blank/partial while
    // saving a draft - only enforce its consistency checks once the log is finalized.
    const skipUnlessFinal = (validator) => {
      return function validateWhenFinal(value, parentVm) {
        return isFinal() ? validator.call(this, value, parentVm) : true;
      };
    };
    // Records created before Procedure Start/End Time went live never collected these
    // fields and the UI hides them for such records (see isNewRecord in medicaid-repeatable.js) -
    // so they must never be validated, even when the log is finalized with a Provider Signature.
    const isOldRecord = () => {
      const createdDate = this.student.form.createdDate;
      if (!createdDate) return false;
      return moment(createdDate).isBefore(moment(Constants.PROCEDURE_START_TIME));
    };
    const skipForOldRecordUnlessFinal = (validator) => {
      return function validateStartEndTime(value, parentVm) {
        if (isOldRecord()) return true;
        return isFinal() ? validator.call(this, value, parentVm) : true;
      };
    };
    const isMinutesProvided = (nestedModel) => {
      return nestedModel.minutes !== null && nestedModel.minutes !== undefined && nestedModel.minutes !== '';
    };
    const requiredWithMinutes = requiredIf((nestedModel) => {
      return !isOldRecord() && isMinutesProvided(nestedModel);
    });
    // The Start/End Time-within-Session check must run any time the field has a value,
    // draft or final - otherwise medicaid-repeatable.js's startTimeChanged()/endTimeChanged()
    // can auto-calculate the paired field off of an out-of-session value with no validation
    // error surfaced, and manually-entered Start/End Time pairs (no Minutes yet) go unchecked.
    const skipForOldRecord = (validator) => {
      return function validateWithinSession(value, parentVm) {
        return isOldRecord() ? true : validator.call(this, value, parentVm);
      };
    };
    const supervisorUserIdValidation = (
      this.action === 'edit' &&
      this.supervisorFlagsArray.canSupervise &&
      this.supervisorFlagsArray.supervisorRequiredFlag
    ) ?
      {
        supervisorUserId: {
          required: requiredIf(isFinal)
        }
      }
      : {};

    return {
      student: {
        form: {
          totalReimbursableMinutes: {
            isTotalReimbursableMinutesValid
          },
          serviceInformation: {
            needLevel: {
              required
            },
            sessionDate: {
              required,
              isWithinOneYear,
              isBetweenStartAndEndDate,
              isNotFutureDate
            },
            sessionTime: {
              required
            },
            sessionTimeEnd: {
              required,
              isSessionTimeEndGreaterThanSessionTime
            },
            sessionMinutes: {
              required,
              areMinutesGreaterThanZero,
              isValidMinutes
            },
            location: {
              required
            },
            deliveryMode: {
              required: requiredIf(isFinal)
            },
            noShow: {
              required: requiredIf((nestedModel) => {
                return nestedModel.needLevel === Constants.ABSENT;
              })
            },
            licenseAttestUserId: {
              required: requiredIf(isFinal)
            },
            ...supervisorUserIdValidation,
            roomName: {
              required: requiredIf((nestedModel) => {
                return nestedModel.deliveryMode === Constants.IN_PERSON && isFinal();
              })
            }
          },
          medicaid: {
            $each: {
              minutes: {
                required: requiredIf(isFinal),
                areMinutesGreaterThanZero: skipUnlessFinal(areMinutesGreaterThanZero),
                isValidMinutes: skipUnlessFinal(isValidMinutes)
              },
              startTime: {
                isValidTime: skipForOldRecordUnlessFinal(isValidTime),
                isProcedureStartTimeWithinSession: skipForOldRecord(isProcedureStartTimeWithinSession),
                required: requiredWithMinutes
              },
              endTime: {
                isValidTime: skipForOldRecordUnlessFinal(isValidTime),
                isEndTimeEndGreaterThanStartTime: skipForOldRecordUnlessFinal(isEndTimeEndGreaterThanStartTime),
                isProcedureEndTimeWithinSession: skipForOldRecord(isProcedureEndTimeWithinSession),
                required: requiredWithMinutes
              },
              procedures: {
                required: requiredIf(isFinal)
              },
              outcome: {
                required: requiredIf(isFinal)
              }
            }
          }
        }
      }
    };
  },
  mounted() {
    EventBus.$on('reset-validations', this.resetForm);
  },
  methods: {
    onCancelButtonClicked() {
      if (this.action === 'edit' || this.action === 'copy') {
        this.$router.go(-1);
      } else {
        this.$emit('remove:student', this.student);
      }
    },
    resetForm() {
      this.$v.student.form.$reset();
      this.errorMsg = '';
    },
    handleSubmit(res) {
      switch (this.saveOptions) {
        case 'next':
          // add the submission to localStorage to display later
          utilityFunctions.addSubmission(res.data.data);
          // remove student from selected-students array
          this.$emit('remove:student');
          this.$v.student.$reset();
          this.saveOptions = null;
          break;
        case 'copy':
          // copies the form for the user to submit again
          utilityFunctions.addSubmission(res.data.data);
          this.student.form.serviceInformation.sessionDate = '';
          this.student.form.serviceInformation.licenseAttestUserId = null;
          this.student.form.serviceInformation.licenseAttestDate = null;
          this.student.form.serviceInformation.licenseAttestName = null;
          this.saveOptions = null;
          break;
        default:
          // default case is 'save', which saves the form and displays submissions
          utilityFunctions.addSubmission(res.data.data);
          this.$router.push('/history');
          break;
      }
    },
    showAlert() {
      return new Promise((resolve, reject) => {
        // if there is a credential name, resolve right away
        if (this.student.form.serviceInformation.credentialName) {
          resolve();
        } else {
          Swal.fire({
            type: 'warning',
            text: 'This provider has no associated credentials.',
            timer: 1000,
            showConfirmButton: false,
            onClose: () => {
              resolve();
            }
          });
        }
      });
    },
    submitForm(student) {
      this.errorMsg = '';
      this.showLoading = true;
      const formSubmission =
      new SubmissionClass(student);
      formSubmission.isDraft = !this.student.form.serviceInformation.licenseAttestUserId;

      this.showAlert()
        .then(() => {
          return api.post(Constants.NSL_CREATE_URL(), formSubmission);
        })
        .then((res) => {
          this.handleSubmit(res);
        })
        .catch((err) => {
          this.errorMsg = err.response.data.message;
        })
        .finally(() => {
          this.showLoading = false;
          Swal.fire({
            type: 'success',
            text: 'Service Log has been successfully submitted!',
            showConfirmButton: false,
            timer: 1000
          });
        });
    },
    editForm() {
      this.errorMsg = '';
      this.showLoading = true;
      const formSubmission =
        new SubmissionClass(this.student);
      formSubmission.isDraft = !this.student.form.serviceInformation.licenseAttestUserId;
      const sessionId = this.$route.params.sessionId;
      // add session id to body to update service log
      formSubmission.sessionId = sessionId;
      return api.put(Constants.NSL_UPDATE_URL(sessionId), formSubmission)
        .then((res) => {
          this.handleSubmit(res);
        })
        .catch((err) => {
          this.errorMsg = err.response.data.message;
        })
        .finally(() => {
          Swal.fire({
            type: 'success',
            text: 'Service Log has been successfully submitted!',
            showConfirmButton: false,
            timer: 1000
          });
          this.showLoading = false;
        });
    },
    submit(student) {
      this.$v.student.form.$touch();
      if (!this.$v.student.form.$invalid) {
        this.$emit('form:submitted');
        if (this.action === 'edit') {
          this.editForm(student);
        } else {
          this.submitForm(student);
        }
      } else {
        // form isnt valid, set an error
        this.errorMsg = 'Please correct the errors above and resubmit the form';
      }
    }
  },
  watch: {
    'student.form.serviceInformation.sessionMinutes': function() {
      if (this.$v.student.form.totalMedicaidMinutes) {
        this.$v.student.form.totalMedicaidMinutes.$touch();
      }
    },
    'student.form.totalReimbursableMinutes': function () {
      if (this.$v.student.form.totalReimbursableMinutes) {
        this.$v.student.form.totalReimbursableMinutes.$touch();
      }
    },
    'student.form.totalMedicaidMinutes': function () {
      if (this.$v.student.form.totalMedicaidMinutes) {
        this.$v.student.form.totalMedicaidMinutes.$touch();
      }
    }
  }
};




// WEBPACK FOOTER //
// ./src/components/service-log/form/service-log-form/service-log-form.js